import React from 'react';
import * as XLSX from 'xlsx';

export interface ExcelColumn<T = any> {
  title?: React.ReactNode | ((...args: any[]) => React.ReactNode);
  dataIndex?: string | number | (string | number)[];
  key?: React.Key;
  width?: number;
  children?: ExcelColumn<T>[];
  render?: (value: any, record: T, index: number) => any;
  [key: string]: any;
}

const getValue = (
  record: any,
  dataIndex?: string | number | (string | number)[]
): any => {
  if (dataIndex === undefined || dataIndex === null) {
    return undefined;
  }

  if (Array.isArray(dataIndex)) {
    return dataIndex.reduce((value, key) => value?.[key], record);
  }

  if (typeof dataIndex === 'string') {
    return dataIndex.split('.').reduce((value, key) => value?.[key], record);
  }

  return record?.[dataIndex];
};

const reactNodeToString = (value: any): string => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (Array.isArray(value)) {
    return value.map(reactNodeToString).filter(Boolean).join(' ');
  }

  if (React.isValidElement(value)) {
    const props = value.props as {
      children?: React.ReactNode;
    };

    return reactNodeToString(props.children);
  }

  return '';
};

const getColumnName = (column: ExcelColumn): string => {
  const title = column.title;

  if (title !== undefined && typeof title !== 'function') {
    const titleText = reactNodeToString(title);

    if (titleText) {
      return titleText;
    }
  }

  if (column.key !== undefined) {
    return String(column.key);
  }

  if (column.dataIndex !== undefined) {
    if (Array.isArray(column.dataIndex)) {
      return column.dataIndex.join('.');
    }

    return String(column.dataIndex);
  }

  return 'Column';
};

/**
 * Convert values into Excel-safe values.
 */
const normalizeValue = (value: any): any => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (Array.isArray(value)) {
    return value.map(normalizeValue).join(', ');
  }

  if (React.isValidElement(value)) {
    return reactNodeToString(value);
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return value;
};

const flattenColumns = <T>(columns: ExcelColumn<T>[]): ExcelColumn<T>[] => {
  const result: ExcelColumn<T>[] = [];

  columns.forEach((column) => {
    if (column.children && column.children.length > 0) {
      result.push(...flattenColumns(column.children));
    } else {
      result.push(column);
    }
  });

  return result;
};

/**
 * Export report to Excel.
 */
export const exportReportToExcel = <T extends Record<string, any>>(
  data: T[],
  columns: ExcelColumn<T>[],
  fileName: string,
  sheetName = 'Report'
): boolean => {
  if (!data || data.length === 0) {
    return false;
  }

  const flattenedColumns = flattenColumns(columns);

  const exportColumns = flattenedColumns.filter(
    (column) => column.dataIndex !== undefined
  );

  const rows = data.map((record, rowIndex) => {
    const row: Record<string, any> = {};

    exportColumns.forEach((column) => {
      const rawValue = getValue(record, column.dataIndex);

      let value = rawValue;
      if (typeof column.render === 'function') {
        try {
          value = column.render(rawValue, record, rowIndex);
        } catch {
          value = rawValue;
        }
      }

      const columnName = getColumnName(column);

      row[columnName] = normalizeValue(value);
    });

    return row;
  });

  const worksheet = XLSX.utils.json_to_sheet(rows);

  worksheet['!cols'] = exportColumns.map((column) => {
    const columnName = getColumnName(column);

    const valuesLength = rows.map(
      (row) => String(row[columnName] ?? '').length
    );

    const longestValue = Math.max(columnName.length, ...valuesLength);

    return {
      wch: Math.min(Math.max(longestValue + 2, 12), 50)
    };
  });

  /**
   * Add Excel autofilter.
   */
  if (worksheet['!ref']) {
    worksheet['!autofilter'] = {
      ref: worksheet['!ref']
    };
  }

  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.substring(0, 31));

  XLSX.writeFile(workbook, `${fileName}.xlsx`);

  return true;
};

export default exportReportToExcel;
